import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../../lib/api";
import AeoDashboard from "../AeoDashboard";
import SeoAuditPanel from "../SeoAuditPanel";
import QuestionLibrary from "./QuestionLibrary";
import ImprovementOpportunities from "./ImprovementOpportunities";
import AccuracyRisks from "./AccuracyRisks";
import MonthlyReport from "./MonthlyReport";
import {
  AiVisibilityOverview, PlatformComparison, QuestionMatrix, CategoryBreakdown, Competitors,
  ResponseTable, Consistency, RefreshButton, ScopeFilter, BaselinePanel,
} from "./AiMonitorPanels";

// One tab, several sections. The brief asked for consolidation rather than an
// ever-longer page, so the existing SEO and manual monitoring work moves under
// sub-navigation instead of being duplicated or replaced.
const SECTIONS = [
  { key: "overview", label: "AI Visibility Overview" },
  { key: "automated", label: "Automated Monitoring" },
  { key: "baseline", label: "Baseline" },
  { key: "platforms", label: "Platform Comparison" },
  { key: "improvements", label: "Improvement Opportunities" },
  { key: "accuracy", label: "Accuracy & Reputation Risks" },
  { key: "competitors", label: "Competitor Visibility" },
  { key: "trends", label: "Historical Trends" },
  { key: "report", label: "Monthly Report" },
  { key: "library", label: "Question Library & Scheduling" },
  { key: "manual", label: "Manual Monitoring" },
  { key: "seo", label: "SEO & Search Performance" },
];

export default function SeoAiVisibilityTab() {
  const [section, setSection] = useState(() => localStorage.getItem("ai-visibility-section") || "overview");
  const [scope, setScope] = useState(() => localStorage.getItem("ai-visibility-scope") || "");
  const [metrics, setMetrics] = useState(null);
  const [trend, setTrend] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/ai-monitor/metrics", { params: scope ? { scope } : {} })
      .then((r) => setMetrics(r.data)).catch(() => setMetrics(null));
    api.get("/admin/ai-monitor/trend", { params: scope ? { scope } : {} })
      .then((r) => setTrend(r.data)).catch(() => setTrend(null));
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  // While a run is in flight the figures move, so the page follows it.
  useEffect(() => {
    if (!metrics?.active_run) return undefined;
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [metrics?.active_run, load]);

  const open = (key) => {
    setSection(key);
    localStorage.setItem("ai-visibility-section", key);
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/ai-monitor/run", {});
      toast.success(`Started: ${res.data.tests_planned} tests, estimated £${res.data.estimated_cost_gbp.toFixed(2)}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The run could not be started.");
    } finally {
      setBusy(false);
    }
  };

  const togglePause = async () => {
    setBusy(true);
    try {
      await api.post("/admin/ai-monitor/settings", { paused: !metrics?.paused });
      toast.success(metrics?.paused ? "Monitoring resumed." : "Monitoring paused. Nothing chargeable will run.");
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="seo-ai-visibility">
      <div className="flex flex-wrap items-center gap-1.5">
        {SECTIONS.map((s) => (
          <button key={s.key} onClick={() => open(s.key)} data-testid={`ai-section-${s.key}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold duration-200 [transition-property:background-color,color] ${
                    section === s.key ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {s.label}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-slate-400 hidden lg:inline">
            filters the overview, platforms, matrix and trends
          </span>
          <ScopeFilter value={scope} onChange={(v) => {
            setScope(v);
            localStorage.setItem("ai-visibility-scope", v);
          }} data={metrics} />
          <RefreshButton onClick={load} busy={busy} />
        </span>
      </div>

      {section === "overview" && (
        <AiVisibilityOverview data={metrics} onRun={runNow} onPause={togglePause} busy={busy} />
      )}

      {section === "automated" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="ai-automated-section">
          <h3 className="font-bold text-navy">Question-level results</h3>
          <p className="text-xs text-slate-500 mt-1 mb-4">
            Every answer is kept in full as evidence, including the model and grounding that produced it. A
            classification can be corrected; the original automatic assessment is kept alongside it.
          </p>
          <ResponseTable month={metrics?.month || ""} />
        </section>
      )}

      {section === "baseline" && <BaselinePanel />}

      {section === "platforms" && (
        <div className="space-y-5">
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="font-bold text-navy">Where CASS is being recommended</h3>
            <div className="mt-4"><PlatformComparison data={metrics} /></div>
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="font-bold text-navy">The same question, platform by platform</h3>
            <p className="text-xs text-slate-500 mt-1">
              Unbranded questions. One platform naming CASS where another does not is a content gap, not a
              measurement error.
            </p>
            <div className="mt-4"><QuestionMatrix data={metrics} /></div>
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="font-bold text-navy">Which questions CASS shows up for</h3>
            <p className="text-xs text-slate-500 mt-1">
              Unbranded questions only. A weak category is where the website may not answer that intent well enough.
            </p>
            <div className="mt-4"><CategoryBreakdown data={metrics} /></div>
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="font-bold text-navy">Consistency between runs</h3>
            <div className="mt-4"><Consistency data={metrics} /></div>
          </section>
        </div>
      )}

      {section === "competitors" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="font-bold text-navy">Firms recommended alongside, or instead of, CASS</h3>
          <p className="text-xs text-slate-500 mt-1 mb-4">
            Taken from the answers themselves. Software, directories and institutes are excluded.
          </p>
          <Competitors data={metrics} />
        </section>
      )}

      {section === "trends" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="ai-trends">
          <h3 className="font-bold text-navy">Visibility over time</h3>
          <p className="text-xs text-slate-500 mt-1">{trend?.note}</p>
          {(trend?.series || []).length === 0 ? (
            <p className="text-sm text-slate-600 mt-4" data-testid="ai-trend-empty">
              A trend needs at least two months of results. The first run establishes the baseline.
            </p>
          ) : (
            <table className="min-w-full text-sm mt-4">
              <thead className="text-slate-600"><tr>
                <th className="text-left py-1">Month</th><th className="text-left py-1">Appeared</th>
                <th className="text-left py-1">Recommended</th><th className="text-left py-1">Cited</th>
                <th className="text-left py-1">Tests</th>
              </tr></thead>
              <tbody>
                {trend.series.map((m) => (
                  <tr key={m.month} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3">{m.month}</td>
                    <td className="py-1.5 pr-3">{((m.appearance_rate || 0) * 100).toFixed(0)}%</td>
                    <td className="py-1.5 pr-3">{((m.recommendation_rate || 0) * 100).toFixed(0)}%</td>
                    <td className="py-1.5 pr-3">{((m.citation_rate || 0) * 100).toFixed(0)}%</td>
                    <td className="py-1.5 pr-3 text-slate-500">{m.tests}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {section === "improvements" && <ImprovementOpportunities />}

      {section === "accuracy" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="ai-accuracy-section">
          <h3 className="font-bold text-navy">What the platforms get wrong about CASS</h3>
          <p className="text-xs text-slate-500 mt-1 mb-4">
            Judged against the verified business facts in the question library settings. Keep those current — they
            are the yardstick.
          </p>
          <AccuracyRisks />
        </section>
      )}

      {section === "report" && <MonthlyReport />}

      {section === "library" && <QuestionLibrary onChanged={load} />}

      {section === "manual" && (
        <section data-testid="ai-manual-section">
          <p className="text-xs text-slate-500 mb-4">
            Checks run by hand on the public apps. These stay separate from the automated results — the public
            answer and the API answer are not the same thing, so this is the control, not a leftover.
            {metrics?.manual_entries ? ` ${metrics.manual_entries} entries recorded so far.` : ""}
          </p>
          <AeoDashboard />
        </section>
      )}

      {section === "seo" && <SeoAuditPanel />}
    </div>
  );
}
